// Firebase Storage adapter for connection and profile photos.
//
// Policy (what counts as a valid image, how large it may be, how it is sized)
// lives in ./imageResize. This module only executes uploads and deletes.

import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { storage, isDemoMode } from "../firebase";
import { photoRejectionReason, resizeImageFile } from "./imageResize";

// Firebase's own messages here are opaque ("An unknown error occurred"), and the
// most likely cause by far is that Storage was never enabled on the project.
const STORAGE_MESSAGES = {
  "storage/unauthorized": "You do not have permission to change this photo.",
  "storage/unauthenticated": "Sign in again to change this photo.",
  "storage/retry-limit-exceeded": "The upload timed out. Check your connection and try again.",
  "storage/canceled": "That upload was cancelled.",
  "storage/quota-exceeded": "Photo storage is full for this project.",
};

/** Turn a Firebase Storage failure into something worth showing a person. */
export function photoErrorMessage(error) {
  const code = error?.code;
  if (STORAGE_MESSAGES[code]) return STORAGE_MESSAGES[code];
  if (code && code.startsWith("storage/")) {
    return "Photos are not available yet — storage is not set up for this app.";
  }
  return error?.message || "That photo could not be saved.";
}

function connectionPhotoRef(ownerUid, entryId) {
  return ref(storage, `rolodex/${ownerUid}/${entryId}/photo.jpg`);
}

function profilePhotoRef(uid) {
  return ref(storage, `profiles/${uid}/photo.jpg`);
}

async function upload(storageRef, file) {
  const reason = photoRejectionReason(file);
  if (reason) throw new Error(reason);

  const resized = await resizeImageFile(file);
  if (isDemoMode) {
    // Demo mode writes nothing; the caller still gets something to render.
    return URL.createObjectURL(resized);
  }
  try {
    await uploadBytes(storageRef, resized, { contentType: "image/jpeg" });
    return await getDownloadURL(storageRef);
  } catch (error) {
    const friendly = new Error(photoErrorMessage(error));
    friendly.cause = error;
    throw friendly;
  }
}

/** Upload a photo for one rolodex entry and return its download URL. */
export function uploadConnectionPhoto(ownerUid, entryId, file) {
  if (!ownerUid || !entryId) {
    return Promise.reject(new Error("An owner and entry are required."));
  }
  return upload(isDemoMode ? null : connectionPhotoRef(ownerUid, entryId), file);
}

/** Remove a connection photo. A missing object is treated as already gone. */
export async function deleteConnectionPhoto(ownerUid, entryId) {
  if (isDemoMode || !ownerUid || !entryId) return;
  try {
    await deleteObject(connectionPhotoRef(ownerUid, entryId));
  } catch (error) {
    // A photo that is already gone is not a failure.
    if (error?.code === "storage/object-not-found") return;
    throw new Error(photoErrorMessage(error));
  }
}

/** Upload the signed-in user's own profile photo. */
export function uploadProfilePhoto(uid, file) {
  if (!uid) return Promise.reject(new Error("A user is required."));
  return upload(isDemoMode ? null : profilePhotoRef(uid), file);
}
